import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Youtube, Mail, Phone, MapPin, Send } from 'lucide-react';
import { useSiteConfig } from '../context/SiteConfigContext';

const DEFAULT_BRAND = 'Victoria Resin Store';

export default function Footer() {
  const { config } = useSiteConfig();

  // config is null until the site-config API call resolves (or if it
  // fails). Render a minimal skeleton footer rather than crashing on
  // config.brand.split(...) etc.
  if (!config) {
    return (
      <footer className="bg-[#1e3a2b] text-neutral-200 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-14 animate-pulse">
          <div className="h-8 w-48 bg-white/10 rounded mb-4" />
          <div className="h-4 w-full max-w-md bg-white/10 rounded mb-2" />
          <div className="h-4 w-2/3 max-w-md bg-white/10 rounded" />
        </div>
      </footer>
    );
  }

  const brand = config.brand || DEFAULT_BRAND;
  const words = brand.split(' ');
  const brandFirst = words[0];
  const brandRest  = words.slice(1).join(' ').toUpperCase() || 'RESIN STORE';
  const socials = config.socials || {};

  return (
    <footer className="bg-[#1e3a2b] text-neutral-200 mt-20">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-6 items-center">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl">Join the {brandFirst} family</h3>
            <p className="text-sm text-neutral-300 mt-1">Early access to new drops, artisan stories and members-only pricing.</p>
          </div>
          <form onSubmit={(e) => e.preventDefault()} className="flex gap-2">
            <input type="email" required placeholder="you@example.com" className="flex-1 px-4 py-3 rounded-md text-neutral-900 focus:outline-none focus:ring-2 focus:ring-[#b8935a]" />
            <button className="bg-[#b8935a] hover:bg-[#a17d47] text-white px-5 py-3 rounded-md font-semibold transition-colors inline-flex items-center gap-2">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-2">
          <Link to="/" className="flex items-center gap-2 mb-4">
            {config.logoUrl ? (
              <img src={config.logoUrl} alt={brand} className="h-10 w-auto" />
            ) : (
              <>
                <span className="font-serif italic text-3xl text-[#b8935a]">{brandFirst}</span>
                <span className="h-6 w-px bg-neutral-500" />
                <span className="text-xs tracking-[0.25em] text-[#b8935a] leading-tight">
                  {brandRest.split(' ').map((w, i, arr) => (
                    <React.Fragment key={i}>{w}{i < arr.length - 1 && <br />}</React.Fragment>
                  ))}
                </span>
              </>
            )}
          </Link>
          <p className="text-sm text-neutral-300 leading-relaxed max-w-md">
            India&#39;s premier destination for handcrafted epoxy resin furniture and art. Each piece is a one-of-a-kind fusion of nature and craftsmanship.
          </p>
          <div className="flex gap-3 mt-5">
            {socials.instagram && <a href={socials.instagram} target="_blank" rel="noreferrer" className="p-2 rounded-full border border-neutral-500 hover:bg-[#b8935a] hover:border-[#b8935a] transition-colors"><Instagram className="w-4 h-4" /></a>}
            {socials.facebook  && <a href={socials.facebook}  target="_blank" rel="noreferrer" className="p-2 rounded-full border border-neutral-500 hover:bg-[#b8935a] hover:border-[#b8935a] transition-colors"><Facebook className="w-4 h-4" /></a>}
            {socials.youtube   && <a href={socials.youtube}   target="_blank" rel="noreferrer" className="p-2 rounded-full border border-neutral-500 hover:bg-[#b8935a] hover:border-[#b8935a] transition-colors"><Youtube className="w-4 h-4" /></a>}
          </div>
        </div>

        <div>
          <h4 className="text-[#b8935a] font-semibold mb-4 text-sm tracking-wider">SHOP</h4>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/collection/dining-table" className="hover:text-[#b8935a] transition-colors">Dining Tables</Link></li>
            <li><Link to="/collection/coffee-table" className="hover:text-[#b8935a] transition-colors">Coffee Tables</Link></li>
            <li><Link to="/collection/wall-art" className="hover:text-[#b8935a] transition-colors">Wall Art</Link></li>
            <li><Link to="/collection/wall-clock" className="hover:text-[#b8935a] transition-colors">Wall Clocks</Link></li>
            <li><Link to="/collection/all" className="hover:text-[#b8935a] transition-colors">All Products</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[#b8935a] font-semibold mb-4 text-sm tracking-wider">COMPANY</h4>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/pages/about-us" className="hover:text-[#b8935a] transition-colors">About Us</Link></li>
            <li><Link to="/contact" className="hover:text-[#b8935a] transition-colors">Contact</Link></li>
            <li><Link to="/account/orders" className="hover:text-[#b8935a] transition-colors">My Orders</Link></li>
            <li><Link to="/account/wishlist" className="hover:text-[#b8935a] transition-colors">Wishlist</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-[#b8935a] font-semibold mb-4 text-sm tracking-wider">POLICIES</h4>
          <ul className="space-y-2 text-sm text-neutral-300">
            <li><Link to="/pages/shipping-policy" className="hover:text-[#b8935a] transition-colors">Shipping Policy</Link></li>
            <li><Link to="/pages/return-refund-policy" className="hover:text-[#b8935a] transition-colors">Return & Refund</Link></li>
            <li><Link to="/pages/cancellation-policy" className="hover:text-[#b8935a] transition-colors">Cancellation</Link></li>
            <li><Link to="/pages/privacy-policy" className="hover:text-[#b8935a] transition-colors">Privacy Policy</Link></li>
            <li><Link to="/pages/terms-and-conditions" className="hover:text-[#b8935a] transition-colors">Terms & Conditions</Link></li>
          </ul>
        </div>
      </div>

      {/* Contact bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 grid sm:grid-cols-3 gap-4 text-sm text-neutral-300">
          <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#b8935a]" /> <a href={`tel:${config.phone || ''}`} className="hover:text-white">{config.phone}</a></div>
          <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#b8935a]" /> <a href={`mailto:${config.email || ''}`} className="hover:text-white break-all">{config.email}</a></div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#b8935a]" /> {config.address}</div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-white/10 bg-black/20">
        <div className="max-w-7xl mx-auto px-6 py-4 text-xs text-neutral-400 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>&copy; {new Date().getFullYear()} {brand}. All rights reserved.</span>
          <span><a href="https://techthetaitsolution.com" target='_blank' rel="noreferrer">Handcrafted with Techtheta IT Solution</a></span>
        </div>
      </div>
    </footer>
  );
}

